/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Queue Profiles module.
----------------------------------------------------------------*/

import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Plus, Search, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { QueueProfileGeneralSection } from './QueueProfileGeneralSection';
import { QueueProfileCallerExperienceSection } from './QueueProfileCallerExperienceSection';
import { QueueProfileTierRulesSection } from './QueueProfileTierRulesSection';
import { QueueProfileFormState } from '../queueProfiles/types';
import { DEFAULT_FORM_STATE } from './constants';
import {
  mapResponseToState,
} from './utils';
import { navigateToQueueProfileDuplicate } from './queueProfilesActions';
import { fetchQueueProfile, createQueueProfile, updateQueueProfile, fetchAllQueueProfiles } from '../queueProfiles/api';
import { QueueProfile } from '../queueProfiles/types';

export default function QueueProfileFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const { showError, showSuccess, showInfo } = useNotifications();

  const [formData, setFormData] = useState<QueueProfileFormState>({ ...DEFAULT_FORM_STATE });
  const [originalData, setOriginalData] = useState<QueueProfileFormState>({ ...DEFAULT_FORM_STATE });
  const settingsRef = useRef<Record<string, unknown>>({});
  const originalSettingsRef = useRef<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Quick List state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListProfiles, setQuickListProfiles] = useState<QueueProfile[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Store stable references to avoid re-running the fetch effect
  const navigateRef = useRef(navigate);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);

  // Update refs when values change (but don't trigger effects)
  useEffect(() => {
    navigateRef.current = navigate;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
    tRef.current = t;
  }, [navigate, showError, showInfo, t]);

  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const profiles = await fetchAllQueueProfiles();
      setQuickListProfiles(profiles.map((p) => ({
        ...p,
        uuid: p.uuid || '',
        settings: p.settings || null,
        createdAt: p.createdAt || null,
      })));
    } catch (error) {
      showErrorRef.current(error?.message || tRef.current('queueProfiles.messages.loadError'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, loadQuickList]);

  // Filter profiles client-side
  const filteredQuickList = useMemo(() => {
    if (!quickListSearch.trim()) {
      return quickListProfiles;
    }
    const searchLower = quickListSearch.toLowerCase();
    return quickListProfiles.filter(
      (profile) =>
        profile.name?.toLowerCase().includes(searchLower) ||
        profile.description?.toLowerCase().includes(searchLower)
    );
  }, [quickListProfiles, quickListSearch]);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (isEditMode && id) {
      navigateToQueueProfileDuplicate(navigate, id);
    }
  }, [isEditMode, id, navigate]);

  useEffect(() => {
    if (isEditMode && id && !duplicateId) {
      return;
    }

    if (duplicateId) {
      // Will be handled by the fetch effect below
      return;
    }

    const defaultState = { ...DEFAULT_FORM_STATE };
    setFormData(defaultState);
    setOriginalData({ ...defaultState });
    settingsRef.current = {};
    originalSettingsRef.current = {};
    setIsLoading(false);
  }, [id, isEditMode, duplicateId]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const defaultState = { ...DEFAULT_FORM_STATE };
      setFormData(defaultState);
      setOriginalData({ ...defaultState });
      settingsRef.current = {};
      originalSettingsRef.current = {};

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, searchParams]);

  useEffect(() => {
    const profileId = duplicateId || (isEditMode ? id : null);
    if (!profileId) {
      return;
    }

    let isActive = true;

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const result = await fetchQueueProfile(profileId);

        if (!isActive) {
          return;
        }

        const { formState, extraSettings } = mapResponseToState(result.data);

        if (duplicateId) {
          // Clear ID for duplicate, modify name
          formState.queueProfileName = `${formState.queueProfileName}_copy`;
          showInfoRef.current(tRef.current('queueProfiles.messages.duplicateHint'));
        }

        setFormData(formState);
        setOriginalData({ ...formState });
        settingsRef.current = { ...extraSettings };
        originalSettingsRef.current = { ...extraSettings };
      } catch (error) {
        if (!isActive) {
          return;
        }
        const message = error instanceof Error ? error.message : tRef.current('queueProfiles.messages.loadError');
        showErrorRef.current(message);
        navigateRef.current('/settings/pbx/queue-profiles', { replace: true });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void fetchProfile();

    return () => {
      isActive = false;
    };
  }, [id, isEditMode, duplicateId]);

  // Memoize hasChanges with a more efficient comparison
  const hasChanges = useMemo(() => {
    // Compare form data fields individually
    const formChanged = Object.keys(formData).some((key) => {
      const formKey = key as keyof QueueProfileFormState;
      return formData[formKey] !== originalData[formKey];
    });

    // Compare settings
    const settingsChanged = JSON.stringify(settingsRef.current) !== JSON.stringify(originalSettingsRef.current);

    return formChanged || settingsChanged;
  }, [formData, originalData]);

  const handleFieldChange = useCallback(
    <K extends keyof QueueProfileFormState>(field: K, value: QueueProfileFormState[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );


  const handleCancel = useCallback(() => {
    setFormData({ ...originalData });
    settingsRef.current = { ...originalSettingsRef.current };
    setErrors({});
  }, [originalData]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    const trimmedName = formData.queueProfileName.trim();
    if (!trimmedName) {
      newErrors.queueProfileName = t('queueProfiles.messages.validationError');
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData.queueProfileName, t]);

  const handleSubmit = useCallback(async (): Promise<QueueProfile | undefined> => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' � ');
      showError(`${t('queueProfiles.messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }
    const trimmedName = formData.queueProfileName.trim();
    const cleanedSettings: Record<string, unknown> = { ...settingsRef.current };

    // Caller Experience / Announcements
    if (formData.callerExitDestination.trim()) {
      cleanedSettings.caller_exit_destination = formData.callerExitDestination.trim();
    }
    cleanedSettings.ringback_tone = formData.ringbackTone.trim() || '${us-ring}';
    if (formData.announceWrapUpMessage.trim()) {
      cleanedSettings.announce_wrap_up_message = formData.announceWrapUpMessage.trim();
    }
    cleanedSettings.announce_resume_playback = formData.announceResumePlayback;
    cleanedSettings.ringback_before_moh = formData.playRingbackBeforeMOH;
    if (formData.agentWhisperTone.trim()) {
      cleanedSettings.agent_whisper = formData.agentWhisperTone.trim();
    }

    // Tier Rules
    cleanedSettings.tier_rules_apply = formData.tierRulesApply;
    cleanedSettings.tier_rule_wait = Number(formData.tierRuleWait) || 5;
    cleanedSettings.tier_rule_multiply_level = formData.tierRuleMultiplyLevel;
    cleanedSettings.tier_rule_no_agent_no_wait = formData.tierRuleNoAgentNoWait;
    cleanedSettings.tier_rule_max_wait = Number(formData.tierRuleMaxWait) || 0;
    if (formData.tierWaitStopSound.trim()) {
      cleanedSettings.tier_wait_stop_sound = formData.tierWaitStopSound.trim();
    }
    if (formData.tierRuleLevelStopSound.trim()) {
      cleanedSettings.tier_rule_level_stop_sound = formData.tierRuleLevelStopSound.trim();
    }
    cleanedSettings.tier_skip_busy_agents = formData.tierSkipBusyAgents;
    cleanedSettings.tier_randomize_agents = formData.tierRandomizeAgents;
    cleanedSettings.reserve_agents = formData.reserveAgents;
    cleanedSettings.truncate_tiers_on_load = formData.truncateTiersOnLoad;
    cleanedSettings.tier_no_wait = formData.tierNoWait;
    cleanedSettings.tier_agent_no_wait = formData.tierAgentNoWait;
    cleanedSettings.tier_retry_wait = Number(formData.tierRetryWait) || 0;
    cleanedSettings.tier_rule_retry_wait = Number(formData.tierRuleRetryWait) || 0;
    cleanedSettings.tier_agent_retry_wait = Number(formData.tierAgentRetryWait) || 0;

    // Advanced Queue Logic
    cleanedSettings.member_delay = Number(formData.memberDelay) || 2;
    cleanedSettings.member_timeout = Number(formData.memberTimeout) || 60;
    cleanedSettings.truncate_agents_on_load = formData.truncateAgentsOnLoad;
    cleanedSettings.log_debug_info = formData.logDebugInfo;
    cleanedSettings.strict_retry = formData.strictRetry;
    cleanedSettings.skip_agents_with_active_call = formData.skipAgentsWithActiveCall;
    cleanedSettings.agent_cooldown = Number(formData.agentCooldownTime) || 0;
    cleanedSettings.agent_busy_delay_time = Number(formData.agentBusyDelay) || 0;
    cleanedSettings.agent_reject_delay_time = Number(formData.agentRejectDelay) || 0;
    cleanedSettings.max_wait_size = Number(formData.maxWaitSize) || 0;
    cleanedSettings.ring_progressively = formData.ringProgressively;
    if (formData.skipCallerIdNumber.trim()) {
      cleanedSettings.skip_call_caller_id_number = formData.skipCallerIdNumber.trim();
    }

    const payload = {
      name: trimmedName,
      description: null as string | null,
      settings: cleanedSettings
    };

    setIsSaving(true);
    try {
      const result = isEditMode && id
        ? await updateQueueProfile(id, payload)
        : await createQueueProfile(payload);

      const { formState, extraSettings } = mapResponseToState(result.data);

      setFormData(formState);
      setOriginalData({ ...formState });
      settingsRef.current = { ...extraSettings };
      originalSettingsRef.current = { ...extraSettings };

      showSuccess(t('queueProfiles.messages.saveSuccess'));

      // Return saved data for useSaveWithAction
      if (result.data?.id) {
        return result.data;
      }
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('queueProfiles.messages.saveError');
      showError(message);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, isEditMode, id, showError, showInfo, showSuccess, t]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as QueueProfile;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/pbx/queue-profiles',
    newPath: '/settings/pbx/queue-profiles/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Queue Profiles use ID for navigation
      return `/settings/pbx/queue-profiles/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'queueProfiles:saveAndNewData',
  });


  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('queueProfiles.form.editTitle')} - ${formData.queueProfileName || ''}` : t('queueProfiles.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('queueProfiles.actions.quickList', 'Quick List'),
            onClick: () => setShowQuickList(true)
          },
          ...(isEditMode && id
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('queueProfiles.actions.add'),
            onClick: () => navigate('/settings/pbx/queue-profiles/new')
          }
        ]}
      />

      <div className="space-y-4">
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">{t('queueProfiles.form.tabs.general')}</TabsTrigger>
            <TabsTrigger value="callerExperience">{t('queueProfiles.form.tabs.callerExperience')}</TabsTrigger>
            <TabsTrigger value="tierRules">{t('queueProfiles.form.tabs.tierRules')}</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <QueueProfileGeneralSection
              formData={formData}
              onFieldChange={handleFieldChange}
            />
          </TabsContent>

          <TabsContent value="callerExperience" className="space-y-4">
            <QueueProfileCallerExperienceSection
              formData={formData}
              onFieldChange={handleFieldChange}
            />
          </TabsContent>

          <TabsContent value="tierRules" className="space-y-4">
            <QueueProfileTierRulesSection
              formData={formData}
              onFieldChange={handleFieldChange}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        saveDisabled={isSaving}
        isLoading={isSaving}
        useSaveDropdown={true}
        saveDropdownStorageKey="queueProfiles:saveActionPreference"
        hasChanges={hasChanges}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('queueProfiles.actions.quickList', 'Queue Profiles')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => {
          setShowQuickList(false);
          navigate(`/settings/pbx/queue-profiles/${profile.id}`);
        }}
        currentItemId={isEditMode ? id : null}
        renderItem={(profile, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{profile.name}</p>
              <p className="text-sm text-muted-foreground">
                {profile.description || t('queueProfiles.noDescription', 'No description')}
              </p>
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('queueProfiles.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('queueProfiles.quickListSearch', 'Search by name or description')}
        emptyMessage={t('queueProfiles.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('queueProfiles.quickListNoResults', 'No profiles found')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

